import { defineNuxtPlugin, useRouter } from '#imports'
import { useAuthStore } from '~/stores/useAuthStore'

export interface GrantsPlugin {
  get(params: { model: string; actions: string[] }): boolean
  all(grants: Record<string, string[]>): boolean
  any(grants: Record<string, string[]>): boolean
  route(path: string): Record<string, string[]> | null | false
  access(path: string): boolean
  readonly role: any
  readonly list: Record<string, string[]> | null
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const isExternalPath = (path: string) => /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(path) || path.startsWith('mailto:') || path.startsWith('tel:')

  const plugin: GrantsPlugin = {
    get({ model, actions }) {
      const active = nuxtApp.$auth.active
      const grants = this.list

      if (!active || !grants) {
        return false
      }

      if (!grants[model]) {
        return false
      }

      for (const action of actions) {
        if (!grants[model].includes(action)) {
          return false
        }
      }

      return true
    },

    all(grants) {
      return Object.entries(grants)
        .every(([model, actions]) => {
          return this.get({ model, actions })
        })
    },

    any(grants) {
      return Object.entries(grants)
        .some(([model, actions]) => {
          const active = nuxtApp.$auth.active
          const grantsList = this.list

          if (!active || !grantsList) {
            return false
          }

          if (!grantsList[model]) {
            return false
          }

          for (const action of actions) {
            if (grantsList[model].includes(action)) {
              return true
            }
          }

          return false
        })
    },

    route(path) {
      if (!path) {
        return false
      }
      if (isExternalPath(path)) {
        return null
      }

      const resolved = router.resolve(path)
      const grants = resolved.meta.grants ?? null

      return grants
    },

    access(path) {
      if (!path) {
        return false
      }
      if (isExternalPath(path)) {
        return true
      }

      const resolved = router.resolve(path)
      const grants = resolved.meta.grants ?? null

      if (!grants) {
        return true
      }

      return this.all(grants)
    },

    get role() {
      return nuxtApp.$auth.active?.info?.employee?.role
    },

    get list() {
      return useAuthStore(nuxtApp.$pinia).grants
    },
  }

  nuxtApp.provide('grants', plugin)
})
